import { GraphQLFieldConfig, GraphQLList, GraphQLNonNull } from 'graphql'
import { filterGroupsToBuckets as applicationMetadataGroupsToBuckets } from '../endpoint-helpers/applications'
import {
  vulnerabilityAggregationType,
  VulnerabilityAggregation
} from '../types/vulnerability-aggregation'
import { Context } from '../shared'
import {
  vulnerabilityAggregationArgs,
  VulnerabilityAggregationArgs,
  vulnerabilitySessionMetadataAggregationArgs,
  VulnerabilitySessionMetadataAggregationArgs
} from '../types/vulnerability-aggregation-args'
import {
  ContrastQueryParameters,
  appendVulnerabilityFiltersToBody
} from '../endpoint-helpers'
import {
  LabeledBucket,
  LabeledBucketWithSubfilters,
  SessionMetadataBuckets,
  SessionMetadataBucketsType
} from '../types/bucket'
import { getRequestedFields } from './helpers/aggregations'

function mapTermBucket({ count, keycode }) {
  return {
    count,
    key: keycode
  }
}

function mapApplicationBucket({ keycode: id, details: { name }, count }) {
  return {
    count,
    key: {
      id,
      name
    }
  }
}

function mapServerBucket({
  keycode: id,
  details: { name, serverpath: path },
  count
}) {
  return {
    count,
    key: {
      id,
      name,
      path
    }
  }
}

function mapLabeledBucket({ keycode: id, label, count }): LabeledBucket {
  return {
    count,
    key: {
      id,
      label
    }
  }
}

function mapLabeledBucketsWithSubfilters({
  keycode: id,
  label,
  count,
  subfilters_id: subfiltersId,
  subfilters
}): LabeledBucketWithSubfilters {
  return { count, key: { id, label, subfiltersId, subfilters } }
}

const extractBuckets = (mapper) => (data) => data.filters.map(mapper)
const extractAppMetadata = (data) =>
  applicationMetadataGroupsToBuckets(data.filters)
const extractTermBuckets = extractBuckets(mapTermBucket)
const extractApplicationBuckets = extractBuckets(mapApplicationBucket)
const extractServerBuckets = extractBuckets(mapServerBucket)
const extractLabeledBuckets = extractBuckets(mapLabeledBucket)
const extractLabeledBucketsWithSubfilters = extractBuckets(
  mapLabeledBucketsWithSubfilters
)

type VulnerabilityBucket =
  | 'tags'
  | 'severity'
  | 'status'
  | 'vulntype'
  | 'appversiontags'
  | 'servers'
  | 'servers-environment'
  | 'url'
  | 'modules'
  | 'security-standard'
  | 'route-coverage'
  | 'technologies'
  | 'application-tags'
  | 'application-importance'
  | 'application-language'
  | 'application-metadata'
  | 'sink'
  | 'bugtracker-status'
  | 'protect-status'

export const vulnerabilityAggregations: GraphQLFieldConfig<
  any,
  Context,
  VulnerabilityAggregationArgs
> = {
  description: 'Get the aggregation for a collection of vulnerabilities',
  type: new GraphQLNonNull(vulnerabilityAggregationType),
  args: vulnerabilityAggregationArgs,
  async resolve(
    _source,
    args,
    context,
    info
  ): Promise<Partial<VulnerabilityAggregation>> {
    const { organizationID, filters } = args
    const { fieldNodes } = info

    const requestedFields = getRequestedFields(
      fieldNodes,
      'vulnerabilityAggregations'
    )

    const hasRequestedField = (field: string) =>
      requestedFields.indexOf(field) > -1

    const params = new ContrastQueryParameters()
    params.append('expand', 'skip_links')

    const mappedFilters = appendVulnerabilityFiltersToBody(filters)

    const getAggregation = (bucketType: VulnerabilityBucket) =>
      context.connector.post(
        `${organizationID}/orgtraces/filter/${bucketType}/listing?${params}`,
        mappedFilters
      )

    /**
     * If there is a single application selected in the filters, we will request
     * 'modules' or 'child applications' of the selected application as an aggregation
     */
    const appID =
      filters &&
      filters.applications &&
      filters.applications.length === 1 &&
      filters.applications[0]
    const getModulesAggregation = () =>
      appID
        ? context.connector.post(
            `${organizationID}/traces/${appID}/filter/modules/listing?${params}`,
            mappedFilters
          )
        : Promise.resolve({ filters: [] })
    const getRoutesAggregation = async () => {
      if (appID) {
        try {
          const result = await context.connector.post(
            `${organizationID}/traces/${appID}/filter/route-coverage/listing?${params}`,
            mappedFilters
          )
          return result
        } catch (_e) {}
      }
      return { filters: [] }
    }

    const [
      tags,
      severities,
      statuses,
      types,
      buildNumbers,
      servers,
      environments,
      urls,
      applications,
      modules,
      securityStandards,
      routes,
      technologies,
      applicationTags,
      applicationMetadata,
      applicationImportances,
      languages,
      sinks,
      bugtrackerStatuses,
      protectStatuses
    ] = await Promise.all([
      hasRequestedField('tags')
        ? getAggregation('tags').then(extractTermBuckets)
        : Promise.resolve([]),
      hasRequestedField('severities')
        ? getAggregation('severity').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('statuses')
        ? getAggregation('status').then(extractLabeledBucketsWithSubfilters)
        : Promise.resolve([]),
      hasRequestedField('types')
        ? getAggregation('vulntype').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('buildNumbers')
        ? getAggregation('appversiontags').then(extractTermBuckets)
        : Promise.resolve([]),
      hasRequestedField('servers')
        ? getAggregation('servers').then(extractServerBuckets)
        : Promise.resolve([]),
      hasRequestedField('environments')
        ? getAggregation('servers-environment').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('urls')
        ? getAggregation('url').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('applications')
        ? getAggregation('modules').then(extractApplicationBuckets)
        : Promise.resolve([]),
      hasRequestedField('modules')
        ? getModulesAggregation().then(extractApplicationBuckets)
        : Promise.resolve([]),
      hasRequestedField('securityStandards')
        ? getAggregation('security-standard').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('routes')
        ? getRoutesAggregation().then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('technologies')
        ? getAggregation('technologies').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('applicationTags')
        ? getAggregation('application-tags').then(extractTermBuckets)
        : Promise.resolve([]),
      hasRequestedField('applicationMetadata')
        ? getAggregation('application-metadata').then(extractAppMetadata)
        : Promise.resolve([]),
      hasRequestedField('applicationImportances')
        ? getAggregation('application-importance').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('languages')
        ? getAggregation('application-language').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('sinks')
        ? getAggregation('sink').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('bugtrackerStatuses')
        ? getAggregation('bugtracker-status').then(extractLabeledBuckets)
        : Promise.resolve([]),
      hasRequestedField('protectStatuses')
        ? getAggregation('protect-status').then(extractLabeledBuckets)
        : Promise.resolve([])
    ])

    return {
      tags,
      severities,
      statuses,
      types,
      buildNumbers,
      servers,
      environments,
      urls,
      applications,
      modules,
      securityStandards,
      routes,
      technologies,
      applicationTags,
      applicationMetadata,
      applicationImportances,
      languages,
      sinks,
      bugtrackerStatuses,
      protectStatuses
    }
  }
}

interface FilterGroup {
  readonly id: string
  readonly fieldType: string
  readonly label: string
  readonly values: Array<{
    readonly value: string
    readonly count: number
  }>
}

function filterGroupsToBuckets(
  groups: FilterGroup[]
): SessionMetadataBuckets[] {
  return groups.map(({ id, fieldType, label, values }) => {
    return {
      id,
      fieldType,
      label,
      buckets: values.map(({ value, count }) => {
        return {
          count,
          key: {
            id: value,
            value
          }
        }
      })
    }
  })
}

export const sessionMetadataAggregations: GraphQLFieldConfig<
  any,
  Context,
  VulnerabilitySessionMetadataAggregationArgs
> = {
  description:
    'Get the aggregations of session metadata values for a collection of vulnerabilities',
  type: new GraphQLNonNull(new GraphQLList(SessionMetadataBucketsType)),
  args: vulnerabilitySessionMetadataAggregationArgs,
  async resolve(_source, args, context): Promise<SessionMetadataBuckets[]> {
    const { organizationID, filters, applicationID } = args

    const params = new ContrastQueryParameters()
    params.append('expand', 'skip_links')

    const mappedFilters = appendVulnerabilityFiltersToBody(filters)

    const { filters: metadataFilters } = await context.connector.post(
      `/${organizationID}/metadata/session/${applicationID}/filters?${params}`,
      mappedFilters
    )

    return filterGroupsToBuckets(metadataFilters)
  }
}
